import fs from 'node:fs';
import path from 'node:path';
import { parse, modify, applyEdits } from 'jsonc-parser';
import { loadFile, writeFile } from 'magicast';
import { addVitePlugin } from 'magicast/helpers';
import {
  AGENT_DEFINITIONS_AGENTS_MD,
  API_AGENTS_MD,
  HANDLERS_AGENTS_MD,
  HOOKS_AGENTS_MD,
  MODELS_AGENTS_MD,
  PROMPTS_AGENTS_MD,
  ROOT_AGENTS_MD,
  TOOLS_AGENTS_MD,
} from '@standardagents/agentbuilder-template';
import { logger } from '../utils/logger.js';

interface ScaffoldOptions {
  force?: boolean;
}

type DependencySection = 'dependencies' | 'devDependencies';

declare const __CLI_VERSION__: string;

function getScaffoldStandardAgentsVersion(): string {
  if (process.env.STANDARDAGENTS_WORKSPACE === '1') {
    return 'workspace:*';
  }
  return typeof __CLI_VERSION__ === 'undefined' ? 'latest' : __CLI_VERSION__;
}

function findViteConfig(cwd: string): string | null {
  const candidates = ['vite.config.ts', 'vite.config.js', 'vite.config.mts', 'vite.config.mjs'];
  for (const candidate of candidates) {
    const configPath = path.join(cwd, candidate);
    if (fs.existsSync(configPath)) {
      return configPath;
    }
  }
  return null;
}

function dependencySectionFor(
  pkg: { dependencies?: Record<string, string>; devDependencies?: Record<string, string> },
  packageName: string,
  fallback: DependencySection,
): DependencySection {
  if (pkg.dependencies?.[packageName] !== undefined) return 'dependencies';
  if (pkg.devDependencies?.[packageName] !== undefined) return 'devDependencies';
  return fallback;
}

function setPackageDependency(
  packageJsonText: string,
  section: DependencySection,
  packageName: string,
  version: string,
): string {
  const edits = modify(packageJsonText, [section, packageName], version, {
    formattingOptions: { insertSpaces: true, tabSize: 2 },
  });
  return applyEdits(packageJsonText, edits);
}

export function updatePackageDependencies(
  cwd: string,
  versions: {
    standardAgents?: string;
  } = {},
): boolean {
  const packageJsonPath = path.join(cwd, 'package.json');
  if (!fs.existsSync(packageJsonPath)) {
    logger.warning('No package.json found. Install @standardagents/builder manually.');
    return false;
  }

  try {
    const text = fs.readFileSync(packageJsonPath, 'utf-8');
    const pkg = JSON.parse(text) as {
      dependencies?: Record<string, string>;
      devDependencies?: Record<string, string>;
    };

    const desiredBuilderVersion = versions.standardAgents ?? getScaffoldStandardAgentsVersion();
    const builderSection = dependencySectionFor(pkg, '@standardagents/builder', 'dependencies');

    let result = text;
    let changed = false;

    if (pkg[builderSection]?.['@standardagents/builder'] !== desiredBuilderVersion) {
      result = setPackageDependency(result, builderSection, '@standardagents/builder', desiredBuilderVersion);
      changed = true;
    }

    if (!changed) {
      logger.info('package.json already has current Standard Agents dependencies');
      return true;
    }

    fs.writeFileSync(packageJsonPath, `${result.trimEnd()}\n`, 'utf-8');
    logger.success('Updated package.json with current Standard Agents dependencies');
    return true;
  } catch (error) {
    logger.warning(`Could not update package.json dependencies: ${error}`);
    return false;
  }
}

async function modifyViteConfig(configPath: string, force: boolean): Promise<boolean> {
  try {
    const mod = await loadFile(configPath);

    const code = fs.readFileSync(configPath, 'utf-8');
    const hasAgentBuilder = code.includes('@standardagents/builder') || code.includes('builder()') || code.includes('agentbuilder()');

    if (hasAgentBuilder && !force) {
      logger.info('Vite config already includes builder plugin');
      return true;
    }

    if (!hasAgentBuilder || force) {
      addVitePlugin(mod, {
        from: '@standardagents/builder',
        imported: 'builder',
        constructor: 'builder',
      });
      logger.success('Added builder plugin to vite.config');
    }

    await writeFile(mod, configPath);
    return true;
  } catch (error) {
    logger.warning(`Could not automatically modify vite.config: ${error}`);
    logger.log('');
    logger.log('Please manually add the following to your vite.config.ts:');
    logger.log('');
    logger.log(`  import { builder } from '@standardagents/builder'`);
    logger.log('');
    logger.log(`  plugins: [builder()]`);
    logger.log('');
    return false;
  }
}

function createDirectoriesAndDocs(cwd: string): void {
  function writeAgentsDocWithClaudeSymlink(dirPath: string, doc: string, label: string): void {
    const agentsDocPath = path.join(dirPath, 'AGENTS.md');
    const claudeDocPath = path.join(dirPath, 'CLAUDE.md');

    if (!fs.existsSync(agentsDocPath)) {
      fs.writeFileSync(agentsDocPath, doc, 'utf-8');
      logger.success(`Created ${label}/AGENTS.md`);
    }

    if (!fs.existsSync(claudeDocPath)) {
      fs.symlinkSync('AGENTS.md', claudeDocPath);
      logger.success(`Created ${label}/CLAUDE.md symlink`);
    }
  }

  // Create project root AGENTS.md and Claude compatibility symlink.
  writeAgentsDocWithClaudeSymlink(cwd, ROOT_AGENTS_MD, '.');

  // Create subdirectories with their AGENTS.md files and Claude compatibility symlinks.
  const directories = [
    { path: 'agents/agents', doc: AGENT_DEFINITIONS_AGENTS_MD },
    { path: 'agents/prompts', doc: PROMPTS_AGENTS_MD },
    { path: 'agents/handlers', doc: HANDLERS_AGENTS_MD },
    { path: 'agents/models', doc: MODELS_AGENTS_MD },
    { path: 'agents/tools', doc: TOOLS_AGENTS_MD },
    { path: 'agents/hooks', doc: HOOKS_AGENTS_MD },
    { path: 'agents/api', doc: API_AGENTS_MD },
  ];

  for (const dir of directories) {
    const dirPath = path.join(cwd, dir.path);

    // Create directory
    if (!fs.existsSync(dirPath)) {
      fs.mkdirSync(dirPath, { recursive: true });
      logger.success(`Created ${dir.path}`);
    }

    writeAgentsDocWithClaudeSymlink(dirPath, dir.doc, dir.path);
  }
}

function updateTsConfig(cwd: string): void {
  const tsconfigPath = path.join(cwd, 'tsconfig.json');

  if (!fs.existsSync(tsconfigPath)) {
    logger.info('No tsconfig.json found, skipping TypeScript configuration');
    return;
  }

  try {
    const text = fs.readFileSync(tsconfigPath, 'utf-8');
    const config = parse(text);
    let result = text;

    const types = config.compilerOptions?.types || [];
    const newTypes = [
      './.agents/types.d.ts',
      './.agents/virtual-module.d.ts'
    ].filter(t => !types.includes(t));

    if (newTypes.length > 0) {
      const allTypes = [...types, ...newTypes];
      const edits = modify(result, ['compilerOptions', 'types'], allTypes, {});
      result = applyEdits(result, edits);
    }

    const include = config.include || [];
    const newIncludes = ['agents', '.agents', 'vite.config.ts'].filter(i => !include.includes(i));

    if (newIncludes.length > 0) {
      const allIncludes = [...include, ...newIncludes];
      const edits = modify(result, ['include'], allIncludes, {});
      result = applyEdits(result, edits);
    }

    if (newTypes.length > 0 || newIncludes.length > 0) {
      fs.writeFileSync(tsconfigPath, result, 'utf-8');
      logger.success('Updated tsconfig.json with Standard Agents types');
    } else {
      logger.info('tsconfig.json already configured');
    }
  } catch (error) {
    logger.warning('Could not update tsconfig.json, you may need to add types manually');
  }
}

function cleanupViteDefaults(cwd: string): void {
  // Remove default Vite files that AgentBuilder no longer needs when scaffold is
  // used as a forced fresh-project conversion. AgentBuilder owns the whole app.
  const filesToRemove = [
    'src/main.ts',
    'src/style.css',
    'src/counter.ts',
    'src/typescript.svg',
    'src/vite-env.d.ts',
    'index.html',
    'public/vite.svg',
    'wrangler.jsonc',
    'wrangler.json',
    'worker/index.ts',
    'agents/Thread.ts',
    'agents/AgentBuilder.ts',
  ];

  const dirsToRemove = ['src', 'public', 'worker'];

  for (const file of filesToRemove) {
    const filePath = path.join(cwd, file);
    if (fs.existsSync(filePath)) {
      try {
        fs.unlinkSync(filePath);
      } catch {
        // Ignore errors
      }
    }
  }

  // Remove empty directories
  for (const dir of dirsToRemove) {
    const dirPath = path.join(cwd, dir);
    if (fs.existsSync(dirPath)) {
      try {
        const files = fs.readdirSync(dirPath);
        if (files.length === 0) {
          fs.rmdirSync(dirPath);
        }
      } catch {
        // Ignore errors
      }
    }
  }
}

export async function scaffold(options: ScaffoldOptions = {}) {
  const cwd = process.cwd();
  const force = options.force || false;

  logger.info('Scaffolding Standard Agents...');
  logger.log('');

  // 1. Find and modify vite.config
  const viteConfigPath = findViteConfig(cwd);
  if (viteConfigPath) {
    await modifyViteConfig(viteConfigPath, force);
  } else {
    logger.warning('No vite.config found. Please create one with the agentbuilder plugin.');
  }

  // 2. Add or refresh package dependencies
  updatePackageDependencies(cwd);

  // 3. Create agent directories with documentation
  createDirectoriesAndDocs(cwd);

  // 4. Update tsconfig.json
  updateTsConfig(cwd);

  // 5. Clean up default Vite and legacy generated files only when explicitly forced.
  if (force) {
    cleanupViteDefaults(cwd);
  }

  logger.log('');
  logger.success('Standard Agents scaffolding complete!');
  logger.log('');
  logger.log('Your project structure:');
  logger.log('');
  logger.log('  AGENTS.md               # Architecture documentation');
  logger.log('  CLAUDE.md -> AGENTS.md  # Compatibility symlink');
  logger.log('  vite.config.ts          # Uses builder(); Cloudflare config is generated');
  logger.log('  tsconfig.json           # Includes agents and generated .agents types');
  logger.log('  agents/');
  logger.log('  ├── agents/             # Agent definitions');
  logger.log('  ├── prompts/            # Prompt definitions');
  logger.log('  ├── handlers/           # Handler definitions');
  logger.log('  ├── models/             # Model configurations');
  logger.log('  ├── tools/              # Custom tools');
  logger.log('  ├── hooks/              # Lifecycle hooks');
  logger.log('  └── api/                # Thread API endpoints');
  logger.log('');
}
